/**
 * The pane's shared history window, Profile-captured (feature-spec §9 decision 4).
 *
 * Its own module because two sections now share it — the Trend section and the
 * Quarantine-by-reason section (REQ-STATUS-100) — and because a hook exported
 * from a file that also exports components breaks Fast Refresh, which
 * `react-refresh/only-export-components` flags and whose advice is literally
 * "use a new file to share constants or functions between components".
 *
 * `SYSTEM_STATUS_SCHEMA_VERSION` moves with it rather than staying behind: the
 * one site that WRITES the `system_status` section is the hook below, and
 * `profile-section-versions.test.ts` requires the stamp to be taken from the
 * constant's single documented home. Splitting the two across modules would
 * either break that invariant or create an import cycle.
 */
import { useContext, useState } from "react";
import type { ProfileDocument } from "@/auth/session-types";
import { PreferencesContext } from "@/preferences/preferences-context";
import {
  DEFAULT_HISTORY_WINDOW,
  HISTORY_WINDOWS,
  type HistoryWindow,
} from "@/system-status/use-component-history";

/**
 * The ``system_status`` section version the BFF treats as current. Every write
 * of the section must carry it (feature-spec §9 decision 4).
 *
 * A stale stamp loses data rather than being cosmetic: the BFF migrates an
 * older section on save exactly as it migrates legacy data. That is how the
 * ``notifications`` section lost every explicit "All" (REQ-UI-130; see
 * ``NOTIFICATION_SCHEMA_VERSION``). This constant is what keeps a future
 * migration of this section from rewriting fresh writes.
 *
 * ``tsc`` cannot hold it to the BFF, because the generated type is ``number``.
 * ``preferences/profile-section-versions.test.ts`` pins it to the ``@default``
 * in ``api-types.gen.ts`` and requires every writer of the section to stamp it.
 */
export const SYSTEM_STATUS_SCHEMA_VERSION: NonNullable<
  ProfileDocument["system_status"]
>["system_status_schema_version"] = 1;

/**
 * The selected Trend window, Profile-captured (feature-spec §9 decision 4).
 *
 * Section collapse on these panes is session-local and folded into the active
 * Profile on save (REQ-UI-131); the window sat beside it as pure session state,
 * so an operator who arranged a workspace got their collapse back and their
 * window reset. The inconsistency was the defect, not the persistence.
 *
 * Three properties, each deliberate:
 *
 *   - **Reads through the working state**, which is what a Profile save
 *     captures, so nothing here needs its own capture path.
 *   - **Optional context.** Read via ``useContext`` rather than
 *     ``usePreferences()``, which throws when unwrapped — the pane must render
 *     in an isolated test and on any surface without a provider, exactly as
 *     ``StatusSectionsProvider`` does. Without one it degrades to local state.
 *   - **An unreadable or absent value resolves to the default.** Every Profile
 *     written before 2026-08-02 lacks the field, and a Profile written while a
 *     since-retired window was valid (`30d`, `90d`) still parses server-side but
 *     must not be *offered* — so the value is validated against
 *     {@link HISTORY_WINDOWS} on the way in rather than trusted.
 *
 * # Shared with the Quarantine-by-reason section (2026-10-06)
 *
 * Exported and renamed from ``useProfileTrendWindow`` when REQ-STATUS-100 added
 * a second windowed section to the same pane. One "how far back am I looking"
 * value per pane, not one per section: two independent windows on one screen
 * invite reading two differently-scoped numbers as if they covered the same
 * span, which is the class of confusion this feature exists to remove.
 *
 * The **storage key stays ``trend_window``** deliberately. Renaming it would be
 * a preferences-schema change — a ``SYSTEM_STATUS_SCHEMA_VERSION`` bump plus a
 * server-side migration — to rename a field whose meaning did not change, and
 * every Profile already written carries the old name.
 *
 */
export function usePaneHistoryWindow(): [HistoryWindow, (next: HistoryWindow) => void] {
  const preferences = useContext(PreferencesContext);
  const stored = preferences?.workingState.system_status?.trend_window;
  const persisted: HistoryWindow = isOfferedWindow(stored) ? stored : DEFAULT_HISTORY_WINDOW;
  // Local mirror so the select responds immediately and still renders with no
  // provider; the write below is what makes the choice outlive the session.
  const [local, setLocal] = useState<HistoryWindow>(persisted);
  const active = preferences === null ? local : persisted;

  const select = (next: HistoryWindow): void => {
    setLocal(next);
    if (preferences === null) return;
    // The stamp must be the CURRENT version: the BFF migrates an older section
    // on save (see SYSTEM_STATUS_SCHEMA_VERSION).
    void preferences.setWorkingState({
      system_status: {
        system_status_schema_version: SYSTEM_STATUS_SCHEMA_VERSION,
        trend_window: next,
      },
    });
  };
  return [active, select];
}

/** Whether a stored value is a window this build actually offers. */
function isOfferedWindow(value: string | undefined): value is HistoryWindow {
  return value !== undefined && (HISTORY_WINDOWS as readonly string[]).includes(value);
}
